import { Combobox as BaseCombobox } from "@base-ui/react/combobox";
import { Popover as PopoverPrimitive } from "@base-ui/react/popover";
import { useDebouncedEffect } from "@react-hookz/web";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
    AlertCircleIcon,
    ArrowRightIcon,
    Loader2,
    Share2Icon,
    XIcon,
} from "lucide-react";
import {
    type FormEvent,
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from "react";
import { toast } from "sonner";

import type {
    IShareableRecipient,
    ISharedConversationBatchResult,
} from "@/api/shared-conversations";
import {
    errorMessageFor,
    GENERIC_SHARE_ERROR_MESSAGE,
    getShareableRecipients,
    SHARE_ERROR_CODES,
} from "@/api/shared-conversations";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
    Combobox,
    ComboboxChips,
    ComboboxChipsInput,
    ComboboxItem,
    ComboboxList,
} from "@/components/ui/combobox";
import { Kbd } from "@/components/ui/kbd";
import { Switch } from "@/components/ui/switch";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { useOrganization } from "@/contexts/frontend/organization/useOrganization";
import type { AccessMode } from "@/hooks/useAccessMode";
import { useShareConversation } from "@/hooks/useShareConversation";
import { useTeamShare } from "@/hooks/useTeamShare";
import {
    trackChatSessionShared,
    trackChatSessionSharedWithTeam,
} from "@/lib/analytics";
import { cn } from "@/lib/utils";

const QUERY_DEBOUNCE_MS = 250;

// Simplified RFC-5322; permissive enough to accept typed free-text emails
// without reimplementing the backend's strict SMTP verifier. Server-side 400
// responses still catch anything that slips through.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * A chosen recipient: either a roster teammate (`IShareableRecipient`, Path A)
 * or a free-typed email not in the org (`{ email }`, Path B / "Invite &
 * share"). The popover keeps an array of these and submits their emails as one
 * batch — the whole point of the multi-select redesign.
 */
type RecipientValue = IShareableRecipient | { email: string };

function isOrgMember(value: RecipientValue): value is IShareableRecipient {
    return "id" in value;
}

function recipientEmail(value: RecipientValue): string {
    return value.email;
}

/** Human label for chips, CTA copy, and inline errors: name for a teammate,
 *  the email itself for a free-typed invite. */
function recipientLabel(value: RecipientValue): string {
    return isOrgMember(value) ? value.display_name : value.email;
}

function initialsOf(displayName: string, email: string): string {
    const parts = displayName.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    if (parts.length === 1 && parts[0].length > 0) {
        return parts[0].slice(0, 2).toUpperCase();
    }
    return (email[0] ?? "?").toUpperCase();
}

export interface ShareConversationButtonProps {
    conversationId: number | string;
    accessMode: AccessMode;
    /**
     * Current team-visibility of the conversation; seeds the (owner-only)
     * "Make visible to entire team" toggle and is the controlled value the
     * toggle's optimistic state reconciles against once the mutation's
     * invalidate round-trip settles. Defaults to `false`. The chat header
     * reads this from the conversation cache via `useConversationTeamVisible`.
     */
    isTeamVisible?: boolean;
}

// ---------------------------------------------------------------------------
// RecipientChipField — base-ui Combobox(multiple) chips + free-solo email
// ---------------------------------------------------------------------------

interface RecipientChipFieldProps {
    selectedRecipients: RecipientValue[];
    onChange: (next: RecipientValue[]) => void;
    rawQuery: string;
    onQueryChange: (next: string) => void;
    roster: IShareableRecipient[];
    isRosterLoading: boolean;
    isRosterError: boolean;
    onRosterRetry: () => void;
    inputId: string;
    describedById: string;
    invalid: boolean;
    disabled: boolean;
    /**
     * Team-share is ON, so the picker is locked: the field is non-interactive,
     * the roster collapses, chips render muted, and the placeholder self-labels.
     * Distinct from `disabled` (a transient submit lock) because it also drives
     * the visual demotion, not just interactivity.
     */
    teamLocked: boolean;
}

/**
 * The chips multi-select. Anatomy note (auto-chevron trap): this is a
 * `ComboboxChips` + `ComboboxChipsInput` field with NO `ComboboxTrigger`, which
 * is the ONLY part that auto-appends a `ChevronDownIcon`. Mirroring
 * `IndustryMultiCombobox`, the field therefore has zero chevrons and the
 * double-chevron trap is structurally impossible — do not add a trigger here.
 *
 * Free-solo: a valid email that matches no roster row is offered as a synthetic
 * "Invite" item so Enter/click adds it as a `{ email }` chip; selecting an
 * already-chosen teammate is blocked by rendering that row `disabled`
 * (`aria-disabled`) rather than hiding it.
 */
function RecipientChipField({
    selectedRecipients,
    onChange,
    rawQuery,
    onQueryChange,
    roster,
    isRosterLoading,
    isRosterError,
    onRosterRetry,
    inputId,
    describedById,
    invalid,
    disabled,
    teamLocked,
}: RecipientChipFieldProps) {
    const trimmed = rawQuery.trim();
    const isValidEmail = EMAIL_RE.test(trimmed);

    const selectedEmails = useMemo(
        () =>
            new Set(
                selectedRecipients.map((r) => recipientEmail(r).toLowerCase())
            ),
        [selectedRecipients]
    );

    const rosterMatch = useMemo(
        () =>
            roster.find(
                (r) => r.email.toLowerCase() === trimmed.toLowerCase()
            ) ?? null,
        [roster, trimmed]
    );

    // A valid, off-roster, not-yet-chosen email becomes the synthetic invite
    // row (Path B). base-ui highlights and selects it like any other item, so
    // Enter adds it without bespoke key handling.
    const createItem = useMemo<RecipientValue | null>(
        () =>
            isValidEmail &&
            !rosterMatch &&
            !selectedEmails.has(trimmed.toLowerCase())
                ? { email: trimmed }
                : null,
        [isValidEmail, rosterMatch, selectedEmails, trimmed]
    );

    // Create-item first so `autoHighlight` lands on it: a fully-typed valid
    // email highlights the "Invite …" row, and Enter adds it without bespoke
    // key handling. (It only exists when the query is a valid, off-roster,
    // not-yet-chosen email, so it never shadows a real roster match.)
    const items = useMemo<RecipientValue[]>(
        () => (createItem ? [createItem, ...roster] : [...roster]),
        [roster, createItem]
    );

    // Render the list whenever there is anything to pick — roster rows OR the
    // synthetic invite row. Critically, this is independent of `isRosterError`:
    // a roster outage still lets the user invite by email (the error banner
    // shows above, the invite row shows below). When team-locked the list
    // collapses entirely — a dimmed full-org roster is the worst "dead box".
    const showList = items.length > 0 && !teamLocked;

    return (
        <Combobox
            multiple
            items={items}
            value={selectedRecipients}
            inputValue={rawQuery}
            onInputValueChange={(next) => onQueryChange(next)}
            onValueChange={(next: RecipientValue[]) => onChange(next)}
            itemToStringLabel={recipientLabel}
            isItemEqualToValue={(a: RecipientValue, b: RecipientValue) =>
                recipientEmail(a).toLowerCase() ===
                recipientEmail(b).toLowerCase()
            }
            // The roster query already filters server-side; disable base-ui's
            // second client-side pass so the synthetic invite row is never
            // filtered out and server email-matches aren't hidden by a
            // display-name substring miss.
            filter={null}
            // Highlight the first row as the query changes so Enter commits the
            // create-item (or top roster match) — the keyboard "add" affordance.
            autoHighlight
            openOnInputClick
            disabled={disabled || teamLocked}
        >
            <ComboboxChips
                className={cn(
                    "min-h-9",
                    invalid &&
                        "border-destructive focus-within:ring-destructive/20"
                )}
            >
                {selectedRecipients.map((recipient) => (
                    <BaseCombobox.Chip
                        key={recipientEmail(recipient)}
                        data-slot="combobox-chip"
                        // Indigo selection pill (`.impeccable.md`): selection is
                        // primary, never copper. While team-locked the pill is
                        // demoted to muted — preserved, but visibly inactive.
                        className={cn(
                            "flex h-5 w-fit items-center gap-1 rounded-sm px-1.5 text-xs font-medium whitespace-nowrap",
                            teamLocked
                                ? "bg-muted text-muted-foreground"
                                : "bg-primary/10 text-primary"
                        )}
                    >
                        <span className="max-w-44 truncate">
                            {recipientLabel(recipient)}
                        </span>
                        <BaseCombobox.ChipRemove
                            aria-label={`Remove ${recipientLabel(recipient)}`}
                            className={cn(
                                "rounded-sm p-0.5",
                                teamLocked
                                    ? "text-muted-foreground/60"
                                    : "text-primary/60 hover:text-primary"
                            )}
                        >
                            <XIcon className="pointer-events-none size-3" />
                        </BaseCombobox.ChipRemove>
                    </BaseCombobox.Chip>
                ))}
                <ComboboxChipsInput
                    id={inputId}
                    aria-label="Recipient"
                    aria-describedby={describedById}
                    aria-invalid={invalid || undefined}
                    // The roster renders inline (no base-ui popup), so base-ui
                    // can't drive aria-expanded — wire it to the list's actual
                    // visibility so AT doesn't announce a phantom "expanded".
                    aria-expanded={showList}
                    placeholder={
                        selectedRecipients.length > 0
                            ? ""
                            : teamLocked
                              ? "Visible to your whole team"
                              : "Type a name or email"
                    }
                />
            </ComboboxChips>

            {isRosterLoading && !showList && (
                <p className="text-muted-foreground mt-1.5 px-1 text-xs">
                    Loading team members…
                </p>
            )}

            {isRosterError ? (
                <div
                    role="status"
                    className="bg-destructive/5 text-destructive mt-1.5 flex items-center gap-2 rounded-md px-2.5 py-2 text-xs"
                >
                    <AlertCircleIcon
                        aria-hidden="true"
                        className="size-3.5 shrink-0"
                    />
                    <span className="flex-1">
                        Couldn't load team members; you can still invite by
                        email.
                    </span>
                    <button
                        type="button"
                        onClick={onRosterRetry}
                        className="font-medium underline-offset-2 hover:underline"
                    >
                        Retry
                    </button>
                </div>
            ) : null}

            {showList && (
                // Inline roster — deliberately NOT a floating `ComboboxContent`
                // popup. A base-ui popup opens its own layer that marks the rest
                // of the share popover (the Share CTA) `inert` while open, so the
                // user couldn't submit without first dismissing the dropdown.
                // Rendering the list inline (as the original popover did) keeps
                // the whole form interactive and the roster always visible.
                <ComboboxList className="border-border/60 mt-1.5 max-h-56 overflow-y-auto rounded-md border p-1">
                    {(item: RecipientValue) => {
                        const email = recipientEmail(item);
                        const alreadyChosen = selectedEmails.has(
                            email.toLowerCase()
                        );
                        if (isOrgMember(item)) {
                            return (
                                <ComboboxItem
                                    key={email}
                                    value={item}
                                    disabled={alreadyChosen}
                                    className="[&_svg]:text-primary cursor-pointer"
                                >
                                    <Avatar className="size-7">
                                        <AvatarFallback className="bg-primary/10 text-primary text-[10px] font-medium">
                                            {initialsOf(
                                                item.display_name,
                                                item.email
                                            )}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="flex min-w-0 flex-1 flex-col leading-tight">
                                        <span className="text-foreground truncate text-sm">
                                            {item.display_name}
                                        </span>
                                        <span className="text-muted-foreground truncate text-xs">
                                            {item.email}
                                        </span>
                                    </div>
                                </ComboboxItem>
                            );
                        }
                        // Synthetic free-solo invite row.
                        return (
                            <ComboboxItem
                                key={email}
                                value={item}
                                className="[&_svg]:text-primary cursor-pointer"
                            >
                                <span className="text-sm">
                                    Invite{" "}
                                    <span className="font-medium">{email}</span>
                                </span>
                            </ComboboxItem>
                        );
                    }}
                </ComboboxList>
            )}
        </Combobox>
    );
}

// ---------------------------------------------------------------------------
// PopoverBody — recipients state, batch submit, partial-success handling
// ---------------------------------------------------------------------------

interface PopoverBodyProps {
    conversationId: number | string;
    onClose: () => void;
    onSuccess: (message: string) => void;
    pendingRef: React.MutableRefObject<boolean>;
    /** Current org-wide visibility; seeds the team toggle's optimistic state. */
    isTeamVisible: boolean;
}

function ctaLabelFor(recipients: RecipientValue[]): string {
    if (recipients.length === 0) {
        return "Share";
    }
    if (recipients.some((r) => !isOrgMember(r))) {
        return "Invite & share";
    }
    return `Share with ${recipients.length} ${
        recipients.length === 1 ? "person" : "people"
    }`;
}

function PopoverBody({
    conversationId,
    onClose,
    onSuccess,
    pendingRef,
    isTeamVisible,
}: PopoverBodyProps) {
    const inputId = useId();
    const hintId = useId();
    const teamToggleId = useId();
    const teamDisclosureId = useId();

    // Team-visibility toggle: optimistic switch state with rollback on error.
    // The switch reflects `optimisticTeamVisible`; the controlled `isTeamVisible`
    // prop (read from the conversation cache by the chat header) reconciles it
    // once the mutation's invalidate round-trip settles.
    const teamShare = useTeamShare(conversationId);
    const [optimisticTeamVisible, setOptimisticTeamVisible] =
        useState(isTeamVisible);
    useEffect(() => {
        setOptimisticTeamVisible(isTeamVisible);
    }, [isTeamVisible]);

    const handleTeamToggle = useCallback(
        (next: boolean) => {
            setOptimisticTeamVisible(next);
            teamShare.mutate(next, {
                onSuccess: (data) => {
                    // Snap to server truth — covers the idempotent case where
                    // `next` and the result agree but no transition happened.
                    setOptimisticTeamVisible(data.conversation.is_team_visible);
                    // The analytics event + success toast fire ONLY on a real
                    // off→on transition; re-enabling and disabling are silent.
                    if (next && data.newly_enabled) {
                        // `conversationId` is `number | string` (the header feeds
                        // it the assistant-ui `remoteId`, a string of unknown
                        // numeric-ness). Guard the coercion the same way ChatHeader
                        // does for its export events — a NaN would serialize to a
                        // corrupt `conversation_id: null` row, so fail the event
                        // closed rather than emit garbage. The toast still fires.
                        const conversationIdNum = Number(conversationId);
                        if (Number.isFinite(conversationIdNum)) {
                            trackChatSessionSharedWithTeam({
                                conversation_id: conversationIdNum,
                                member_count: data.notified_count,
                            });
                        }
                        toast.success("Shared with team", {
                            description:
                                data.notified_count > 0
                                    ? `${data.notified_count} ${
                                          data.notified_count === 1
                                              ? "teammate"
                                              : "teammates"
                                      } notified`
                                    : "No other members yet",
                        });
                    }
                },
                onError: (error) => {
                    // Undo the optimistic flip so the UI stays truthful, and
                    // name the rate-limit case specifically (shared bucket).
                    setOptimisticTeamVisible(!next);
                    toast.error(
                        error.response?.status === 429
                            ? "You're toggling too fast — try again in a moment."
                            : "Couldn't update team visibility. Please try again."
                    );
                },
            });
        },
        [conversationId, teamShare]
    );

    const [rawQuery, setRawQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");
    useDebouncedEffect(
        () => setDebouncedQuery(rawQuery),
        [rawQuery],
        QUERY_DEBOUNCE_MS
    );

    const [selectedRecipients, setSelectedRecipients] = useState<
        RecipientValue[]
    >([]);
    // Per-recipient failures from the last batch, keyed by lowercased email.
    const [inlineErrors, setInlineErrors] = useState<Record<string, string>>(
        {}
    );
    const [genericError, setGenericError] = useState(false);
    const submitGuardRef = useRef(false);

    // Fire the `q=""` roster fetch on mount so the full org roster is the
    // popover's initial state, not a "type to search" empty UI.
    // `keepPreviousData` holds the prior result while the next debounced query
    // is in flight so the list never flashes empty between keystrokes.
    const recipients = useQuery({
        queryKey: ["shareableRecipients", debouncedQuery],
        queryFn: ({ signal }) =>
            getShareableRecipients(debouncedQuery, 20, signal),
        staleTime: 30_000,
        placeholderData: keepPreviousData,
    });
    const mutation = useShareConversation(conversationId);

    // Mirror mutation.isPending into the parent's ref so the popover refuses
    // outside-click / Esc dismissal while a batch is in flight; otherwise the
    // body unmounts mid-request and the user never sees the result.
    useEffect(() => {
        pendingRef.current = mutation.isPending;
        return () => {
            pendingRef.current = false;
        };
    }, [mutation.isPending, pendingRef]);

    const roster = useMemo(
        () => recipients.data?.items ?? [],
        [recipients.data]
    );

    const trimmed = rawQuery.trim();
    const hasUnaddedText = trimmed.length > 0;
    const isValidEmail = EMAIL_RE.test(trimmed);
    const rosterMatch = roster.some(
        (r) => r.email.toLowerCase() === trimmed.toLowerCase()
    );
    // Typed text that is neither a valid email nor a roster match is a
    // validation miss — surfaced as inline guidance, not a chip.
    const hasValidationError = hasUnaddedText && !isValidEmail && !rosterMatch;

    // Team-share is the single source of the "is the picker locked?" signal —
    // it follows the optimistic toggle, so the lock flips the instant the switch
    // moves and reconciles with server truth on settle (R5).
    const teamLocked = optimisticTeamVisible;

    const canSubmit =
        selectedRecipients.length > 0 &&
        !mutation.isPending &&
        !genericError &&
        !teamLocked;

    const handleRecipientsChange = useCallback((next: RecipientValue[]) => {
        setSelectedRecipients(next);
        setRawQuery("");
        setInlineErrors({});
        setGenericError(false);
    }, []);

    const handleQueryChange = useCallback((next: string) => {
        setRawQuery(next);
        setGenericError(false);
    }, []);

    const handleSubmit = useCallback(
        (e?: FormEvent<HTMLFormElement>) => {
            e?.preventDefault();
            if (
                selectedRecipients.length === 0 ||
                mutation.isPending ||
                submitGuardRef.current
            ) {
                return;
            }
            submitGuardRef.current = true;
            setInlineErrors({});
            setGenericError(false);

            // Resolve each result's human label off the chips the user chose;
            // batch failures only carry an email, so the roster display name is
            // here, not on the response.
            const labelByEmail = new Map(
                selectedRecipients.map((r) => [
                    recipientEmail(r).toLowerCase(),
                    recipientLabel(r),
                ])
            );

            mutation.mutate(
                { emails: selectedRecipients.map(recipientEmail) },
                {
                    onSuccess: (data) => {
                        // Clear the close-gate synchronously; the effect
                        // mirroring isPending lags a render and would refuse
                        // the onClose() below.
                        pendingRef.current = false;
                        submitGuardRef.current = false;

                        const succeeded = data.results.filter(
                            (r) => r.status === "ok"
                        );
                        const failed = data.results.filter(
                            (r) => r.status === "error"
                        );

                        succeeded.forEach((r) =>
                            trackChatSessionShared({
                                conversation_id: conversationId,
                                share_mode: r.mode ?? "org_member",
                            })
                        );

                        if (succeeded.length > 0) {
                            const count = `${succeeded.length} ${
                                succeeded.length === 1 ? "person" : "people"
                            }`;
                            toast.success(`Shared with ${count}`, {
                                description:
                                    failed.length > 0
                                        ? `${failed.length} couldn't be shared — see the form.`
                                        : undefined,
                            });
                        }

                        if (failed.length === 0) {
                            onSuccess(`Shared with ${succeeded.length}`);
                            onClose();
                            return;
                        }

                        // Partial success: keep the popover open, drop the
                        // succeeded chips, and surface a per-recipient inline
                        // error for each failure (keyed by code) so the broker
                        // can correct or remove it.
                        const failedEmails = new Set(
                            failed.map((r) => r.email.toLowerCase())
                        );
                        setSelectedRecipients((prev) =>
                            prev.filter((r) =>
                                failedEmails.has(
                                    recipientEmail(r).toLowerCase()
                                )
                            )
                        );
                        setInlineErrors(
                            buildInlineErrors(failed, labelByEmail)
                        );
                        onSuccess(
                            `Shared with ${succeeded.length}; ${failed.length} failed`
                        );
                    },
                    onError: () => {
                        pendingRef.current = false;
                        submitGuardRef.current = false;
                        setGenericError(true);
                    },
                }
            );
        },
        [
            mutation,
            selectedRecipients,
            onClose,
            onSuccess,
            pendingRef,
            conversationId,
        ]
    );

    const handleRetry = useCallback(() => {
        setGenericError(false);
        handleSubmit();
    }, [handleSubmit]);

    const inlineErrorList = Object.entries(inlineErrors);
    const hintMessage = hasValidationError
        ? "Enter a valid email address."
        : null;

    return (
        <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-3"
            role="dialog"
            aria-label="Share conversation"
        >
            <header className="flex flex-col gap-0.5">
                <h2 className="text-foreground text-[13px] font-semibold tracking-tight">
                    Share this conversation
                </h2>
                <p className="text-muted-foreground text-[11px] leading-snug">
                    Add teammates or invite by email — everyone you add is
                    shared at once.
                </p>
            </header>

            <div className="border-border/60 flex flex-col gap-1.5 border-b pb-3">
                <div className="flex items-center justify-between gap-3">
                    <label
                        htmlFor={teamToggleId}
                        className="text-foreground cursor-pointer text-[13px] font-medium"
                    >
                        Make visible to entire team
                    </label>
                    <Switch
                        id={teamToggleId}
                        checked={optimisticTeamVisible}
                        onCheckedChange={handleTeamToggle}
                        disabled={teamShare.isPending}
                        aria-label="Make visible to entire team"
                        aria-describedby={teamDisclosureId}
                    />
                </div>
                <p
                    id={teamDisclosureId}
                    className="text-muted-foreground text-[11px] leading-snug"
                >
                    All team members will be notified and can view this
                    conversation.
                </p>
            </div>

            <div
                className="flex flex-col gap-1.5"
                data-testid="individual-share-section"
                aria-disabled={teamLocked || undefined}
            >
                <label htmlFor={inputId} className="sr-only">
                    Recipient
                </label>
                {/* Only the interactive picker goes `inert` while team-locked, so
                    the field + chips + roster leave the tab order. The hint region
                    below stays live — it holds the one escape-hatch affordance. */}
                <div inert={teamLocked}>
                    <RecipientChipField
                        selectedRecipients={selectedRecipients}
                        onChange={handleRecipientsChange}
                        rawQuery={rawQuery}
                        onQueryChange={handleQueryChange}
                        roster={roster}
                        isRosterLoading={
                            recipients.isLoading && roster.length === 0
                        }
                        isRosterError={
                            recipients.isError && !mutation.isPending
                        }
                        onRosterRetry={() => recipients.refetch()}
                        inputId={inputId}
                        describedById={hintId}
                        invalid={hasValidationError}
                        disabled={mutation.isPending}
                        teamLocked={teamLocked}
                    />
                </div>

                <div
                    id={hintId}
                    role="status"
                    aria-live="polite"
                    className={cn(
                        "flex items-start gap-1.5 text-[11px] leading-snug break-words",
                        (hintMessage || genericError) && !teamLocked
                            ? "text-destructive"
                            : "text-muted-foreground"
                    )}
                >
                    {teamLocked ? (
                        // Locked-state copy doubles as the escape hatch: it names
                        // why the picker is paused AND turns team-share back off
                        // (the only way to reach specific people / outside guests).
                        <span className="min-w-0">
                            Everyone on your team can already see this.{" "}
                            <button
                                type="button"
                                onClick={() => handleTeamToggle(false)}
                                // Gated on the in-flight toggle exactly like the
                                // Switch above: while an enable mutation is
                                // pending the escape hatch is the only other live
                                // control, so leaving it clickable lets a
                                // switch-ON-then-link-OFF double-action race —
                                // the two mutations would then reconcile in
                                // network-completion order, not intent order, and
                                // could strand team visibility opposite the
                                // user's final click.
                                disabled={teamShare.isPending}
                                className="text-brand-copper focus-visible:ring-ring/50 inline-flex items-center gap-0.5 rounded-sm font-medium underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-60"
                            >
                                Share with specific people instead
                                <ArrowRightIcon
                                    aria-hidden="true"
                                    className="size-3"
                                />
                            </button>
                        </span>
                    ) : hintMessage ? (
                        <>
                            <AlertCircleIcon
                                aria-hidden="true"
                                className="mt-px size-3 shrink-0"
                            />
                            <span className="min-w-0">{hintMessage}</span>
                        </>
                    ) : genericError ? (
                        <>
                            <AlertCircleIcon
                                aria-hidden="true"
                                className="mt-px size-3 shrink-0"
                            />
                            <span className="min-w-0">
                                {GENERIC_SHARE_ERROR_MESSAGE}{" "}
                                <button
                                    type="button"
                                    onClick={handleRetry}
                                    disabled={mutation.isPending}
                                    className="text-destructive underline-offset-2 hover:underline disabled:opacity-50"
                                >
                                    Retry
                                </button>
                            </span>
                        </>
                    ) : (
                        <span>
                            They'll receive an email with a link to this chat.
                        </span>
                    )}
                </div>
            </div>

            {inlineErrorList.length > 0 && (
                <ul
                    role="status"
                    aria-live="polite"
                    className="flex flex-col gap-1"
                >
                    {inlineErrorList.map(([email, message]) => (
                        <li
                            key={email}
                            className="text-destructive flex items-start gap-1.5 text-[11px] leading-snug"
                        >
                            <AlertCircleIcon
                                aria-hidden="true"
                                className="mt-px size-3 shrink-0"
                            />
                            <span className="min-w-0">{message}</span>
                        </li>
                    ))}
                </ul>
            )}

            <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground text-[11px]">
                    <Kbd className="mr-1">esc</Kbd>
                    to cancel
                </span>
                <Button
                    type="submit"
                    disabled={!canSubmit}
                    aria-busy={mutation.isPending}
                    data-testid="share-cta"
                >
                    {mutation.isPending && (
                        <Loader2
                            aria-hidden="true"
                            className="size-4 animate-spin"
                        />
                    )}
                    {mutation.isPending
                        ? "Sharing…"
                        : ctaLabelFor(selectedRecipients)}
                </Button>
            </div>
        </form>
    );
}

/** Map each failed result to its inline copy, resolving the recipient label off
 *  the chips the user chose (failures carry only an email). An unrecognized
 *  code falls back to the generic line rather than crashing the render. */
function buildInlineErrors(
    failed: ISharedConversationBatchResult[],
    labelByEmail: Map<string, string>
): Record<string, string> {
    const next: Record<string, string> = {};
    for (const result of failed) {
        const key = result.email.toLowerCase();
        const label = labelByEmail.get(key) ?? result.email;
        // A KNOWN typed code maps to its specific copy. A missing code OR one
        // this client doesn't recognize (the wire `code` is an open string, so
        // the backend can ship a new code before the frontend learns it) falls
        // back to the generic line — never the raw machine token `errorMessageFor`
        // would echo from its `never` default for an off-union value.
        next[key] =
            result.code && SHARE_ERROR_CODES.includes(result.code)
                ? errorMessageFor(result.code, label)
                : GENERIC_SHARE_ERROR_MESSAGE;
    }
    return next;
}

export function ShareConversationButton(props: ShareConversationButtonProps) {
    const { accessMode, conversationId, isTeamVisible = false } = props;
    // UX hint only; the batch endpoint re-validates server-side and returns a
    // per-recipient `share_disabled` code if the flag is stale on the client.
    const { settings } = useOrganization();
    const [open, setOpen] = useState(false);
    const [lastStatus, setLastStatus] = useState<string | null>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    // Block popover close while a batch is in flight so an outside-click or Esc
    // can't silently drop the request; onSuccess/onError closes it.
    const pendingRef = useRef(false);
    // Scopes the popup subtree so the Popup's `initialFocus` can resolve the
    // recipient input (see the `initialFocus` prop below).
    const popupRef = useRef<HTMLDivElement>(null);

    if (accessMode !== "owner") {
        return null;
    }
    // Order matters: kill-switch gate runs after the owner gate so
    // collaborators stay hidden regardless of flag state.
    if (!settings.share_polly_enabled) {
        return null;
    }

    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen && pendingRef.current) {
            return;
        }
        if (nextOpen && !open) {
            // Reset the live-region status BEFORE the popover mounts so an
            // identical follow-up message still mutates the DOM (React bails on
            // Object.is, otherwise the announcement never re-fires for AT).
            setLastStatus(null);
        }
        setOpen(nextOpen);
        if (!nextOpen) {
            queueMicrotask(() => triggerRef.current?.focus());
        }
    };

    // While the popover is open, strip the trigger's accessible name so the
    // single in-popover CTA is the only `name: /share/i` button (the trigger
    // stays focusable, so Esc/submit still return focus to it).
    const triggerLabel = open ? undefined : "Share";

    return (
        <>
            <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
                <Tooltip>
                    <TooltipTrigger
                        render={
                            <PopoverPrimitive.Trigger
                                ref={triggerRef}
                                render={
                                    <button
                                        type="button"
                                        aria-label={triggerLabel}
                                        className="hover:bg-accent hover:text-accent-foreground inline-flex size-6 cursor-pointer items-center justify-center rounded-md p-1 transition-colors"
                                    />
                                }
                            >
                                <Share2Icon
                                    className="size-4"
                                    aria-hidden="true"
                                />
                            </PopoverPrimitive.Trigger>
                        }
                    />
                    <TooltipContent side="bottom">Share</TooltipContent>
                </Tooltip>

                <PopoverPrimitive.Portal>
                    <PopoverPrimitive.Positioner
                        side="bottom"
                        sideOffset={6}
                        align="end"
                        className="isolate z-50"
                    >
                        <PopoverPrimitive.Popup
                            ref={popupRef}
                            // The team toggle now sits above the recipient input,
                            // so base-ui's default "focus the first tabbable" would
                            // land on the toggle. Direct initial focus to the
                            // recipient input instead — adding people is the primary
                            // action, and a keyboard user expects to type a name the
                            // instant the popover opens.
                            initialFocus={() =>
                                popupRef.current?.querySelector<HTMLElement>(
                                    '[data-slot="combobox-chip-input"]'
                                ) ?? null
                            }
                            className="bg-popover text-popover-foreground ring-foreground/10 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 motion-reduce:zoom-in-100 data-closed:animate-out data-closed:fade-out-0 w-[22rem] max-w-[calc(100vw-1.5rem)] origin-(--transform-origin) rounded-xl p-4 text-sm shadow-lg ring-1 outline-hidden duration-150"
                        >
                            {open && (
                                <PopoverBody
                                    conversationId={conversationId}
                                    onClose={() => handleOpenChange(false)}
                                    onSuccess={(msg) => setLastStatus(msg)}
                                    pendingRef={pendingRef}
                                    isTeamVisible={isTeamVisible}
                                />
                            )}
                        </PopoverPrimitive.Popup>
                    </PopoverPrimitive.Positioner>
                </PopoverPrimitive.Portal>
            </PopoverPrimitive.Root>
            <div
                role="status"
                aria-live="polite"
                className="sr-only"
                data-testid="share-status"
            >
                {lastStatus}
            </div>
        </>
    );
}
